import { useState, useEffect, useRef, useCallback } from 'react';
import Editable from '../Editable';
import PartnerImageDrop from '../PartnerImageDrop';
import { useSection } from '../useSection';
import { useDragReorder } from '../useDragReorder';
import { authFetch } from '../../auth';
import { ContactFormPreview } from '../FormPreview';

const API = '/api/cms/partners';

export default function Partners({ onRegisterSave, onRegisterDirty, refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const ref = useRef([]);
  const delRef = useRef([]);
  const dirtyRef = useRef(false);
  const { data: ui, setField, saveSection, isDirty: uiIsDirty } = useSection('partners', 'ui', 1);

  useEffect(() => {
    let alive = true;
    authFetch(`${API}?lang=ko`).then((r) => r.json())
      .then((d) => { if (alive) { ref.current = d; setItems(d); } })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [refreshKey]);

  const update = (i, f, v) => {
    dirtyRef.current = true;
    ref.current = ref.current.map((x, idx) => (idx === i ? { ...x, [f]: v } : x));
    setItems(ref.current);
  };

  const addCard = (type = 0) => {
    dirtyRef.current = true;
    const nc = {
      intNum: null,
      _tmpId: `tmp-${Date.now()}`,
      name: type === 0 ? '새 파트너사' : '새 고객사',
      link: '',
      type,
      logoKey: '',
      sort: ref.current.length + 1,
      homeShow: false,
    };
    ref.current = [...ref.current, nc];
    setItems(ref.current);
  };

  const delCard = (idx) => {
    const p = ref.current[idx];
    if (p.intNum != null) {
      if (!window.confirm('이 카드를 삭제할까요? (저장 시 반영)')) return;
      delRef.current.push(p.intNum);
    }
    dirtyRef.current = true;
    ref.current = ref.current.filter((_, i) => i !== idx);
    setItems(ref.current);
  };

  const reorderPartners = useCallback((fromLocal, toLocal) => {
    dirtyRef.current = true;
    const arr = [...ref.current];
    const gIdx = arr.reduce((acc, x, i) => { if ((x.type ?? 0) === 0) acc.push(i); return acc; }, []);
    const fromG = gIdx[fromLocal];
    const toG = gIdx[toLocal];
    const [moved] = arr.splice(fromG, 1);
    arr.splice(toG > fromG ? toG - 1 : toG, 0, moved);
    ref.current = arr.map((x, i) => ({ ...x, sort: i + 1 }));
    setItems(ref.current);
  }, []);

  const reorderCustomers = useCallback((fromLocal, toLocal) => {
    dirtyRef.current = true;
    const arr = [...ref.current];
    const gIdx = arr.reduce((acc, x, i) => { if ((x.type ?? 0) === 1) acc.push(i); return acc; }, []);
    const fromG = gIdx[fromLocal];
    const toG = gIdx[toLocal];
    const [moved] = arr.splice(fromG, 1);
    arr.splice(toG > fromG ? toG - 1 : toG, 0, moved);
    ref.current = arr.map((x, i) => ({ ...x, sort: i + 1 }));
    setItems(ref.current);
  }, []);

  const partnerDrag = useDragReorder(reorderPartners);
  const customerDrag = useDragReorder(reorderCustomers);

  const save = useCallback(async () => {
    for (const id of delRef.current) {
      const res = await authFetch(`${API}/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`파트너 삭제 실패 (${res.status})`);
    }
    delRef.current = [];
    for (const p of ref.current) {
      const res = await authFetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intNum: p.intNum, name: p.name, link: p.link, type: p.type ?? 0, logoKey: p.logoKey, sort: p.sort, homeShow: p.homeShow }),
      });
      if (!res.ok) throw new Error(`파트너 저장 실패 (${res.status})`);
    }
    await saveSection();
    dirtyRef.current = false;
  }, [saveSection]);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);

  const isDirty = useCallback(() => dirtyRef.current || uiIsDirty(), [uiIsDirty]);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  if (items === null) {
    return <section className="section pt-page"><div className="container"><p className="heading-sub color-muted">불러오는 중…</p></div></section>;
  }

  const partnerItems = items.map((p, globalIdx) => ({ p, globalIdx })).filter(({ p }) => (p.type ?? 0) === 0);
  const customerItems = items.map((p, globalIdx) => ({ p, globalIdx })).filter(({ p }) => p.type === 1);

  const renderCard = (p, globalIdx, localIdx, drag) => {
    const fk = p.intNum != null ? `p-${p.intNum}` : p._tmpId;
    return (
      <div className={`s-card ${drag.cardClass(localIdx)}`} key={fk} data-flip={fk} {...drag.dragProps(localIdx)} {...drag.dropProps(localIdx)}>
        <span className="drag-handle" title="카드를 드래그하여 순서 변경">⠿</span>
        <button type="button" className="slide-del-btn" title="카드 삭제" onClick={() => delCard(globalIdx)}>×</button>
        <PartnerImageDrop value={p.logoKey} onChange={(k) => update(globalIdx, 'logoKey', k)} />
        <Editable as="h3" value={p.name} onChange={(t) => update(globalIdx, 'name', t)} />
        <div className="partner-type-row">
          <label className="partner-type-label">구분</label>
          <select
            className="partner-type-select"
            value={p.type ?? 0}
            onChange={(e) => update(globalIdx, 'type', Number(e.target.value))}
          >
            <option value={0}>파트너사</option>
            <option value={1}>고객사</option>
          </select>
        </div>
        <div className="partner-link-row">
          <span className="partner-link-label">Link</span>
          <input
            type="text"
            className="partner-link-input"
            placeholder="https://example.com"
            value={p.link || ''}
            onChange={(e) => update(globalIdx, 'link', e.target.value)}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <section className="section pt-page">
        <div className="container">
          <div className="mb-2xl">
            <Editable as="h1" className="text-6xl" value={ui.title || '파트너사'} onChange={(t) => setField('title', t)} />
            <Editable as="p" className="text-lg mt-xl" value={ui.desc || 'AceTec은 세계 유수의 기술 기업들과의 전략적 파트너십을 통해, 검증된 글로벌 기술과 깊이 있는 국내 엔지니어링 전문성을 결합하여 미션 크리티컬 솔루션을 제공합니다.'} onChange={(t) => setField('desc', t)} />
          </div>

          <div className="mb-2xl">
            <h2 className="text-3xl mb-lg">파트너사</h2>
            <div className="svc-cards" ref={partnerDrag.containerRef}>
              {partnerItems.map(({ p, globalIdx }, localIdx) => renderCard(p, globalIdx, localIdx, partnerDrag))}
              <button type="button" className="card-add-tile" onClick={() => addCard(0)}>+ 파트너사 카드 추가</button>
            </div>
          </div>

          <div>
            <h2 className="text-3xl mb-lg">고객사</h2>
            <div className="svc-cards" ref={customerDrag.containerRef}>
              {customerItems.map(({ p, globalIdx }, localIdx) => renderCard(p, globalIdx, localIdx, customerDrag))}
              <button type="button" className="card-add-tile" onClick={() => addCard(1)}>+ 고객사 카드 추가</button>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-bright section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-txt">
              <Editable as="h2" value={ui.ctaTitle || '파트너십 문의'} onChange={(t) => setField('ctaTitle', t)} />
              <Editable as="p" value={ui.ctaDesc || '기술 협력 또는 파트너십에 관심이 있으신가요? 요구사항을 보내주시면 당사 팀이 영업일 기준 1일 이내에 회신드립니다.'} onChange={(t) => setField('ctaDesc', t)} />
            </div>
            <ContactFormPreview />
          </div>
        </div>
      </section>
    </>
  );
}
